// Gestion du bloc info sticky quand MainContent atteint le top
class ProductStickyInfo {
  constructor() {
    // Désactiver sur mobile/tablette (< 768px)
    if (window.innerWidth < 768) {
      console.log('ProductStickyInfo disabled on mobile/tablet');
      return;
    }

    this.mainContent = document.getElementById('MainContent');
    this.productInfo = document.querySelector('.m-main-product--info');
    this.productMedia = document.querySelector('.m-main-product--media');

    if (!this.mainContent || !this.productInfo || !this.productMedia) {
      console.error('MainContent, product info or product media not found');
      return;
    }

    this.lastScrollY = window.scrollY;
    this.isAnimating = false;
    this.scrollDirection = null;
    this.currentPosition = 'up';
    this.stickyActivatedScrollY = null;
    this.scrollThreshold = 150;
    this.isFrozen = false;

    // Écouter le resize pour désactiver si on passe en mobile
    this.handleResize = this.onResize.bind(this);
    window.addEventListener('resize', this.handleResize);

    this.init();
  }

  onResize() {
    // Si on passe en dessous de 768px, détruire l'instance
    if (window.innerWidth < 768) {
      this.destroy();
    }
  }

  destroy() {
    if (this.mainContentObserver) {
      this.mainContentObserver.disconnect();
    }
    if (this.handleScroll) {
      window.removeEventListener('scroll', this.handleScroll);
    }
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    // Réinitialiser les styles
    if (this.productInfo) {
      this.productInfo.style.removeProperty('position');
      this.productInfo.style.removeProperty('top');
      this.productInfo.style.removeProperty('max-height');
      this.productInfo.style.removeProperty('overflow');
      this.productInfo.classList.remove('m-sticky-active');
    }
  }

  init() {
    // Observer pour détecter quand MainContent atteint le top
    this.mainContentObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const rect = entry.target.getBoundingClientRect();
        const isAtTop = rect.top <= 0;

        if (isAtTop) {
          this.activateSticky();
        } else {
          this.deactivateSticky();
        }
      });
    }, { root: null, rootMargin: '0px', threshold: [0, 1] });

    this.mainContentObserver.observe(this.mainContent);

    // Écouter le scroll
    this.handleScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    this.checkPosition();
  }

  onScroll() {
    const currentScrollY = window.scrollY;
    const rect = this.mainContent.getBoundingClientRect();
    const isAtTop = rect.top <= 0;

    // Activer/désactiver sticky
    if (isAtTop && !this.isSticky) {
      this.activateSticky();
    } else if (!isAtTop && this.isSticky) {
      this.deactivateSticky();
    }

    // Vérifier la position du bas du bloc photos
    const mediaRect = this.productMedia.getBoundingClientRect();
    const bottomIsInViewport = mediaRect.bottom <= window.innerHeight && mediaRect.bottom > 0;
    const bottomIsOutOfViewport = mediaRect.bottom <= 0 || mediaRect.bottom > window.innerHeight;

    // Gestion du freeze/unfreeze basée sur la position du bloc photos
    if (bottomIsInViewport && !this.isFrozen) {
      this.freezeAnimation();
    } else if (bottomIsOutOfViewport && this.isFrozen) {
      this.unfreezeAnimation();
    }

    // Si sticky est actif ET que l'animation n'est PAS gelée
    if (this.isSticky && this.stickyActivatedScrollY !== null && !this.isFrozen) {
      const scrolledSinceSticky = currentScrollY - this.stickyActivatedScrollY;
      const currentDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';

      // SÉCURITÉ : Si on remonte et qu'on est sous le seuil, forcer le scroll UP
      if (currentDirection === 'up' && scrolledSinceSticky < this.scrollThreshold) {
        if (this.currentPosition === 'down') {
          this.scrollDirection = 'up';
          this.animateInfoScroll('up', true);
        }
      }
      // Vérifier si on a dépassé le seuil pour déclencher normalement
      else if (Math.abs(scrolledSinceSticky) >= this.scrollThreshold) {
        if (currentDirection !== this.scrollDirection && currentDirection !== this.currentPosition) {
          this.scrollDirection = currentDirection;
          this.animateInfoScroll(currentDirection, true);
        }
      }
    }

    this.lastScrollY = currentScrollY;
  }

  checkPosition() {
    const rect = this.mainContent.getBoundingClientRect();
    const isAtTop = rect.top <= 0;

    if (isAtTop && !this.isSticky) {
      this.activateSticky();
    } else if (!isAtTop && this.isSticky) {
      this.deactivateSticky();
    }
  }

  animateInfoScroll(direction, force = false) {
    if (this.isAnimating && !force) return;

    if (force && this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }

    this.isAnimating = true;

    const innerContent = this.productInfo.querySelector('.m-product-info--wrapper');
    if (!innerContent) {
      this.isAnimating = false;
      return;
    }

    const contentRect = innerContent.getBoundingClientRect();
    const contentHeight = contentRect.height;

    const crossSell = innerContent.querySelector('.m-product-cross-sell');
    let bottomMargin = 0;
    if (crossSell) {
      const crossSellStyle = window.getComputedStyle(crossSell);
      bottomMargin = parseInt(crossSellStyle.marginBottom) || 0;
    }

    const containerHeight = this.productInfo.clientHeight;
    const totalContentHeight = contentHeight + bottomMargin;

    // Si le contenu tient entièrement, pas d'animation
    if (totalContentHeight <= containerHeight) {
      this.isAnimating = false;
      return;
    }

    const maxTranslate = -(totalContentHeight - containerHeight);
    const targetTranslate = direction === 'down' ? maxTranslate : 0;

    innerContent.style.setProperty('transition', 'transform 1s ease', 'important');
    innerContent.style.setProperty('transform', `translateY(${targetTranslate}px)`, 'important');

    this.currentPosition = direction;

    this.animationTimeout = setTimeout(() => {
      this.isAnimating = false;
    }, 1000);
  }

  activateSticky() {
    if (this.isSticky) return;

    this.isSticky = true;
    this.stickyActivatedScrollY = window.scrollY;

    this.productInfo.style.setProperty('position', 'sticky', 'important');
    this.productInfo.style.setProperty('top', '0', 'important');
    this.productInfo.style.setProperty('max-height', '100vh', 'important');
    this.productInfo.style.setProperty('overflow', 'hidden', 'important');

    this.productInfo.classList.add('m-sticky-active');
  }

  deactivateSticky() {
    if (!this.isSticky) return;

    this.isSticky = false;
    this.stickyActivatedScrollY = null;
    this.scrollDirection = null;
    this.currentPosition = 'up';

    this.productInfo.style.setProperty('position', 'relative', 'important');
    this.productInfo.style.removeProperty('top');
    this.productInfo.style.removeProperty('max-height');
    this.productInfo.style.removeProperty('overflow');
    this.productInfo.classList.remove('m-sticky-active');
  }

  freezeAnimation() {
    if (this.isFrozen) return;

    this.isFrozen = true;

    // Forcer la position 'down' si on n'y est pas déjà
    if (this.currentPosition !== 'down') {
      this.animateInfoScroll('down', true);
    }
  }

  unfreezeAnimation() {
    if (!this.isFrozen) return;

    this.isFrozen = false;

    // Réinitialiser la direction et la position pour permettre une nouvelle animation
    this.scrollDirection = null;
  }
}

// Initialiser au chargement de la page
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductStickyInfo();
  });
} else {
  new ProductStickyInfo();
}
